import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Skeleton,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    AddOutlined,
    ArrowDownwardOutlined,
    ArrowUpwardOutlined,
    CampaignOutlined,
    DeleteOutlineOutlined,
    EditOutlined,
    FormatBoldOutlined,
    InsertLinkOutlined,
    TuneOutlined,
    VisibilityOutlined,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { showToast } from "utils/reusableToast";
import {
    SECTION_LABELS,
    DEFAULT_DELOITTE_OVERAGE_RATE,
} from "pages/admin/components/CopilotDashboard/useDashboardSettings";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import {
    useGetAdminAnnouncementsQuery,
    useCreateAnnouncementMutation,
    useUpdateAnnouncementMutation,
    useDeleteAnnouncementMutation,
    useReorderAnnouncementsMutation,
} from "state/rtkQuery/announcements/announcementQueries";
import { SEVERITY_STYLES, alertSx, renderNoticeMessage } from "utils/announcements/announcementDisplay";
import { useCurrentUserRoleNames } from "components/RequirePermission/useAccessPermissions";
import { isEcrOpsWriteRole } from "components/RequirePermission/RequireEcrOpsRole";

import "./EcrOpsPortalConfig.css";

const ACCENT = "#26890d";
const TITLE_MAX_LENGTH = 120;
const MESSAGE_MAX_LENGTH = 500;
const ROW_TITLE_DISPLAY_LENGTH = 60;
const ROW_MESSAGE_DISPLAY_LENGTH = 120;

const truncate = (text, maxLength) =>
    text && text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;

// Re-exported so existing imports of renderNoticeMessage from this module
// keep working — the implementation now lives in utils/announcements, shared
// with HomeDashboard.js so the live preview here always matches what users see.
export { renderNoticeMessage };

const EMPTY_NOTICE = {
    id: null,
    title: "",
    message: "",
    severity: "info",
    dismissible: true,
    enabled: true,
};

// ── Notice preview (exactly as rendered on the Home Dashboard) ───────────────

const NoticePreviewAlert = ({ notice }) => (
    <Alert
        severity={notice.severity}
        onClose={notice.dismissible ? () => { } : undefined}
        sx={alertSx(notice.severity)}
    >
        {notice.title ? <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 700 }}>{notice.title}</AlertTitle> : null}
        {renderNoticeMessage(notice.message)}
    </Alert>
);

NoticePreviewAlert.propTypes = {
    notice: PropTypes.shape({
        title: PropTypes.string,
        message: PropTypes.string.isRequired,
        severity: PropTypes.string.isRequired,
        dismissible: PropTypes.bool,
    }).isRequired,
};

// ── Announcement editor dialog ───────────────────────────────────────────────

const NoticeEditorDialog = ({ open, notice, onSave, onClose, saving, canWrite }) => {
    const [draft, setDraft] = useState(EMPTY_NOTICE);
    const [linkFieldsOpen, setLinkFieldsOpen] = useState(false);
    const [linkText, setLinkText] = useState("");
    const [linkUrl, setLinkUrl] = useState("");
    const messageInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            // notice.title can be null (backend uses null to mean "no title") —
            // the editor always works with a string internally, only converting
            // an empty title back to null when the save payload is built.
            setDraft(notice ? { ...notice, title: notice.title ?? "" } : EMPTY_NOTICE);
            setLinkFieldsOpen(false);
            setLinkText("");
            setLinkUrl("");
        }
    }, [open, notice]);

    const insertIntoMessage = (snippet) => {
        const input = messageInputRef.current;
        const message = draft.message;
        const start = input?.selectionStart ?? message.length;
        const end = input?.selectionEnd ?? start;
        setDraft((d) => ({
            ...d,
            message: (message.slice(0, start) + snippet + message.slice(end)).slice(0, MESSAGE_MAX_LENGTH),
        }));
    };

    const handleBold = () => {
        const input = messageInputRef.current;
        const message = draft.message;
        const start = input?.selectionStart ?? message.length;
        const end = input?.selectionEnd ?? start;
        const selected = message.slice(start, end) || "bold text";
        setDraft((d) => ({
            ...d,
            message: `${message.slice(0, start)}**${selected}**${message.slice(end)}`.slice(0, MESSAGE_MAX_LENGTH),
        }));
    };

    const handleInsertLink = () => {
        if (!linkUrl.trim()) return;
        insertIntoMessage(`[${linkText.trim() || "link"}](${linkUrl.trim()})`);
        setLinkText("");
        setLinkUrl("");
        setLinkFieldsOpen(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
                {notice ? "Edit Announcement" : "New Announcement"}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    <TextField
                        label="Title (optional)"
                        value={draft.title}
                        onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value.slice(0, TITLE_MAX_LENGTH) }))}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: TITLE_MAX_LENGTH }}
                        helperText={`${draft.title.length}/${TITLE_MAX_LENGTH}`}
                        FormHelperTextProps={{ sx: { textAlign: "right", mr: 0 } }}
                    />

                    <Box>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#555", mb: 0.5 }}>
                            Severity
                        </Typography>
                        <ToggleButtonGroup
                            value={draft.severity}
                            exclusive
                            size="small"
                            onChange={(e, value) => {
                                if (value) setDraft((d) => ({ ...d, severity: value }));
                            }}
                        >
                            {Object.entries(SEVERITY_STYLES).map(([key, s]) => (
                                <ToggleButton
                                    key={key}
                                    value={key}
                                    sx={{
                                        textTransform: "none",
                                        fontSize: "0.78rem",
                                        px: 1.5,
                                        "&.Mui-selected": {
                                            background: s.background,
                                            color: s.color,
                                            fontWeight: 700,
                                            "&:hover": { background: s.background },
                                        },
                                    }}
                                >
                                    {s.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#555" }}>
                                Message
                            </Typography>
                            <Box>
                                <Tooltip title="Bold selected text">
                                    <IconButton size="small" onClick={handleBold}>
                                        <FormatBoldOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Insert hyperlink at cursor">
                                    <IconButton
                                        size="small"
                                        onClick={() => setLinkFieldsOpen((v) => !v)}
                                        sx={{ color: linkFieldsOpen ? ACCENT : undefined }}
                                    >
                                        <InsertLinkOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <TextField
                            value={draft.message}
                            onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value.slice(0, MESSAGE_MAX_LENGTH) }))}
                            inputRef={messageInputRef}
                            multiline
                            minRows={3}
                            fullWidth
                            size="small"
                            placeholder="Supports **bold** and [link text](https://url) — use the toolbar buttons above."
                            inputProps={{ maxLength: MESSAGE_MAX_LENGTH }}
                            helperText={`${draft.message.length}/${MESSAGE_MAX_LENGTH}`}
                            FormHelperTextProps={{ sx: { textAlign: "right", mr: 0 } }}
                        />
                        {linkFieldsOpen && (
                            <Box className="ecr-portal-config__link-fields">
                                <TextField
                                    label="Link text"
                                    value={linkText}
                                    onChange={(e) => setLinkText(e.target.value)}
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="URL"
                                    value={linkUrl}
                                    onChange={(e) => setLinkUrl(e.target.value)}
                                    size="small"
                                    placeholder="https://"
                                    sx={{ flex: 2 }}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleInsertLink}
                                    disabled={!linkUrl.trim()}
                                    sx={{ textTransform: "none", borderColor: ACCENT, color: ACCENT, whiteSpace: "nowrap" }}
                                >
                                    Insert
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#555", mb: 0.5 }}>
                            Preview — how users will see it
                        </Typography>
                        {draft.message.trim() ? (
                            <NoticePreviewAlert notice={draft} />
                        ) : (
                            <Typography sx={{ fontSize: "0.8rem", color: "#999", fontStyle: "italic" }}>
                                Start typing a message to see the preview.
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={draft.dismissible}
                                    onChange={(e) => setDraft((d) => ({ ...d, dismissible: e.target.checked }))}
                                    size="small"
                                    sx={{ "&.Mui-checked": { color: ACCENT } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: "0.82rem" }}>Users can dismiss</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={draft.enabled}
                                    onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                                    size="small"
                                    sx={{ "&.Mui-checked": { color: ACCENT } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: "0.82rem" }}>Enabled</Typography>}
                        />
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: "none", color: "#666" }}>
                    Cancel
                </Button>
                <Tooltip title={!canWrite ? "Only Operations Admin / Enterprise Admin can make changes" : ""}>
                    <span>
                        <Button
                            variant="contained"
                            disabled={!draft.message.trim() || saving || !canWrite}
                            onClick={() => onSave(draft)}
                            sx={{ textTransform: "none", backgroundColor: ACCENT, "&:hover": { backgroundColor: "#1f6e0b" } }}
                        >
                            Save
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

NoticeEditorDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    notice: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    saving: PropTypes.bool,
    canWrite: PropTypes.bool,
};

// ── Announcements tab ────────────────────────────────────────────────────────

const AnnouncementsTab = () => {
    // Falls back to the default (first) platform until the ECR Ops platform
    // selector (EcrOpsLayout) has resolved a selection.
    const { data: platformData } = useGetPlatformsQuery();
    const selectedPlatformId = useSelector((state) => state.ecrOpsPlatform?.selectedPlatformId);
    const platformId = selectedPlatformId || platformData?._id;
    const userEmail = useSelector((state) => state.auth?.userAccount?.username);

    const { data: notices = [], isLoading, isFetching } = useGetAdminAnnouncementsQuery(platformId, {
        skip: !platformId,
        refetchOnMountOrArgChange: true,
    });
    const [createAnnouncement, { isLoading: isCreating }] = useCreateAnnouncementMutation();
    const [updateAnnouncement, { isLoading: isUpdating }] = useUpdateAnnouncementMutation();
    const [deleteAnnouncement, { isLoading: isDeleting }] = useDeleteAnnouncementMutation();
    const [reorderAnnouncements] = useReorderAnnouncementsMutation();

    // Route access to /ecr-ops (RequireEcrOpsRole) admits all 3 ecr-ops roles,
    // but writes here are narrower — OperationsUser can view every
    // announcement but not create/edit/enable/reorder/delete one; only
    // OperationsAdmin/EnterpriseAdmin can.
    const roleNames = useCurrentUserRoleNames();
    const canWrite = isEcrOpsWriteRole(roleNames);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editingNotice, setEditingNotice] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const sorted = [...notices].sort((a, b) => a.order - b.order);
    const liveNotices = sorted.filter((n) => n.enabled);

    const openEditor = (notice = null) => {
        setEditingNotice(notice);
        setEditorOpen(true);
    };

    // Pydantic-style validation errors show up as an array under either
    // `error_message` or `detail`, each entry shaped like { msg, loc, ... } —
    // must be flattened to a string, never rendered as a raw object.
    const extractErrorDescription = (error) => {
        const data = error?.data;
        for (const field of [data?.error_message, data?.detail]) {
            if (typeof field === "string") return field;
            if (Array.isArray(field)) {
                const msg = field.map((d) => (typeof d === "string" ? d : d?.msg)).filter(Boolean).join("; ");
                if (msg) return msg;
            }
        }
        return "Please try again.";
    };

    const handleApiError = (error, fallbackTitle) => {
        showToast({
            title: fallbackTitle,
            description: extractErrorDescription(error),
            type: "error",
            duration: 5000,
        });
    };

    const handleSave = async (draft) => {
        try {
            if (draft.id) {
                // PUT is a partial update: omitting a field means "leave it
                // unchanged," so a cleared title must be sent as `null` (not
                // omitted) or the backend won't clear the existing value.
                await updateAnnouncement({
                    platformId,
                    id: draft.id,
                    title: draft.title.trim() || null,
                    message: draft.message,
                    severity: draft.severity,
                    dismissible: draft.dismissible,
                    enabled: draft.enabled,
                    updated_by: userEmail,
                }).unwrap();
            } else {
                await createAnnouncement({
                    platformId,
                    title: draft.title.trim() || null,
                    message: draft.message,
                    severity: draft.severity,
                    dismissible: draft.dismissible,
                    enabled: draft.enabled,
                    updated_by: userEmail,
                }).unwrap();
            }
            setEditorOpen(false);
            showToast({
                title: draft.id ? "Announcement updated" : "Announcement created",
                type: "success",
                duration: 4000,
            });
        } catch (error) {
            handleApiError(error, draft.id ? "Failed to update announcement" : "Failed to create announcement");
        }
    };

    const toggleEnabled = async (notice) => {
        try {
            await updateAnnouncement({
                platformId,
                id: notice.id,
                enabled: !notice.enabled,
                updated_by: userEmail,
            }).unwrap();
        } catch (error) {
            handleApiError(error, "Failed to update announcement");
        }
    };

    const move = async (id, dir) => {
        const idx = sorted.findIndex((n) => n.id === id);
        const swapWith = idx + dir;
        if (swapWith < 0 || swapWith >= sorted.length) return;
        const reordered = [...sorted];
        [reordered[idx], reordered[swapWith]] = [reordered[swapWith], reordered[idx]];
        try {
            await reorderAnnouncements({ platformId, orderedIds: reordered.map((n) => n.id) }).unwrap();
        } catch (error) {
            handleApiError(error, "Failed to reorder announcements");
        }
    };

    const confirmDelete = async () => {
        try {
            await deleteAnnouncement({ platformId, id: deleteTarget.id }).unwrap();
            setDeleteTarget(null);
            showToast({ title: "Announcement deleted", type: "success", duration: 4000 });
        } catch (error) {
            setDeleteTarget(null);
            handleApiError(error, "Failed to delete announcement");
        }
    };

    if (isLoading) {
        return (
            <div>
                <div className="ecr-portal-config__card">
                    <div className="ecr-portal-config__card-header">
                        <VisibilityOutlined fontSize="small" />
                        <span>Home Dashboard Preview</span>
                    </div>
                    <Stack spacing={1}>
                        <Skeleton variant="rounded" height={44} />
                        <Skeleton variant="rounded" height={44} />
                    </Stack>
                </div>

                <div className="ecr-portal-config__card">
                    <div className="ecr-portal-config__card-header">
                        <CampaignOutlined fontSize="small" />
                        <span>Manage Announcements</span>
                    </div>
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="ecr-portal-config__row">
                            <div className="ecr-portal-config__row-reorder" style={{ flexShrink: 0 }}>
                                <Skeleton variant="circular" width={20} height={20} sx={{ my: "2px" }} />
                                <Skeleton variant="circular" width={20} height={20} sx={{ my: "2px" }} />
                            </div>
                            <Skeleton variant="rounded" width={72} height={24} sx={{ flexShrink: 0 }} />
                            <div className="ecr-portal-config__row-body">
                                <Skeleton variant="text" width="30%" />
                                <Skeleton variant="text" width="70%" />
                            </div>
                            <Skeleton variant="rounded" width={34} height={20} sx={{ flexShrink: 0 }} />
                            <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
                            <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Live preview — what the Home Dashboard renders */}
            <div className="ecr-portal-config__card">
                <div className="ecr-portal-config__card-header">
                    <VisibilityOutlined fontSize="small" />
                    <span>Home Dashboard Preview</span>
                    <span className="ecr-portal-config__hint">
                        Enabled announcements, in display order — exactly as users see them.
                    </span>
                </div>
                {liveNotices.length > 0 ? (
                    <Stack spacing={1}>
                        {liveNotices.map((n) => (
                            <NoticePreviewAlert key={n.id} notice={n} />
                        ))}
                    </Stack>
                ) : (
                    <Typography sx={{ fontSize: "0.85rem", color: "#999", fontStyle: "italic" }}>
                        No enabled announcements — the Home Dashboard would show no notice banners.
                    </Typography>
                )}
            </div>

            {/* Management list */}
            <div className="ecr-portal-config__card">
                <div className="ecr-portal-config__card-header">
                    <CampaignOutlined fontSize="small" />
                    <span>Manage Announcements</span>
                    <Tooltip title={!canWrite ? "Only Operations Admin / Enterprise Admin can make changes" : ""}>
                        <span style={{ marginLeft: "auto" }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddOutlined />}
                                onClick={() => openEditor(null)}
                                disabled={isFetching || !canWrite}
                                sx={{
                                    textTransform: "none",
                                    backgroundColor: ACCENT,
                                    "&:hover": { backgroundColor: "#1f6e0b" },
                                }}
                            >
                                New Announcement
                            </Button>
                        </span>
                    </Tooltip>
                </div>

                {sorted.length === 0 && (
                    <Typography sx={{ fontSize: "0.85rem", color: "#999", fontStyle: "italic", py: 1 }}>
                        No announcements yet — create one to show a banner on the Home Dashboard.
                    </Typography>
                )}

                {sorted.map((n, idx) => {
                    const s = SEVERITY_STYLES[n.severity] ?? SEVERITY_STYLES.info;
                    return (
                        <div key={n.id} className={`ecr-portal-config__row${n.enabled ? "" : " disabled"}`}>
                            <div className="ecr-portal-config__row-reorder" style={{ flexShrink: 0 }}>
                                <IconButton size="small" disabled={idx === 0 || !canWrite} onClick={() => move(n.id, -1)}>
                                    <ArrowUpwardOutlined sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton size="small" disabled={idx === sorted.length - 1 || !canWrite} onClick={() => move(n.id, 1)}>
                                    <ArrowDownwardOutlined sx={{ fontSize: 16 }} />
                                </IconButton>
                            </div>
                            <Chip
                                label={s.label}
                                size="small"
                                sx={{ background: s.background, color: s.color, fontWeight: 700, fontSize: "0.7rem", width: 72, flexShrink: 0 }}
                            />
                            <div className="ecr-portal-config__row-body">
                                <Tooltip title={n.title || ""} disableHoverListener={!n.title}>
                                    <span className="ecr-portal-config__row-title">
                                        {n.title ? truncate(n.title, ROW_TITLE_DISPLAY_LENGTH) : <i style={{ color: "#999" }}>No title</i>}
                                    </span>
                                </Tooltip>
                                <Tooltip title={n.message}>
                                    <span className="ecr-portal-config__row-message">
                                        {truncate(n.message, ROW_MESSAGE_DISPLAY_LENGTH)}
                                    </span>
                                </Tooltip>
                            </div>
                            {!n.dismissible && (
                                <Chip
                                    label="Not dismissible"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: "0.68rem", color: "#888", flexShrink: 0 }}
                                />
                            )}
                            <Tooltip
                                title={
                                    !canWrite
                                        ? "Only Operations Admin / Enterprise Admin can make changes"
                                        : n.enabled
                                            ? "Visible on the Home Dashboard — toggle to hide"
                                            : "Hidden — toggle to show"
                                }
                            >
                                <span>
                                    <Switch
                                        checked={n.enabled}
                                        onChange={() => toggleEnabled(n)}
                                        size="small"
                                        disabled={!canWrite}
                                        inputProps={{ "aria-label": `Toggle ${n.title || n.id}` }}
                                        sx={{
                                            flexShrink: 0,
                                            "& .Mui-checked": { color: `${ACCENT} !important` },
                                            "& .Mui-checked + .MuiSwitch-track": { backgroundColor: `${ACCENT} !important` },
                                        }}
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip title={!canWrite ? "Only Operations Admin / Enterprise Admin can make changes" : "Edit"}>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={`Edit ${n.title || n.id}`}
                                        onClick={() => openEditor(n)}
                                        disabled={!canWrite}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={!canWrite ? "Only Operations Admin / Enterprise Admin can make changes" : "Delete"}>
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={`Delete ${n.title || n.id}`}
                                        onClick={() => setDeleteTarget(n)}
                                        disabled={!canWrite}
                                        sx={{ color: "#b91c1c", flexShrink: 0 }}
                                    >
                                        <DeleteOutlineOutlined fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </div>
                    );
                })}
            </div>

            <NoticeEditorDialog
                open={editorOpen}
                notice={editingNotice}
                onSave={handleSave}
                onClose={() => setEditorOpen(false)}
                saving={isCreating || isUpdating}
                canWrite={canWrite}
            />

            {/* Delete confirmation */}
            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>Delete announcement?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
                        {deleteTarget?.title || "This announcement"} will be removed for all users. This cannot be
                        undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: "#666" }}>
                        Cancel
                    </Button>
                    <Tooltip title={!canWrite ? "Only Operations Admin / Enterprise Admin can make changes" : ""}>
                        <span>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={confirmDelete}
                                disabled={isDeleting || !canWrite}
                                sx={{ textTransform: "none" }}
                            >
                                Delete
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        </div>
    );
};

// ── Copilot dashboard settings tab ───────────────────────────────────────────

const DEFAULT_VISIBLE_SECTIONS = Object.fromEntries(
    Object.keys(SECTION_LABELS).map((key) => [key, true])
);

const DashboardSettingsTab = () => {
    const [visibleSections, setVisibleSections] = useState(DEFAULT_VISIBLE_SECTIONS);
    const [rateInput, setRateInput] = useState(String(DEFAULT_DELOITTE_OVERAGE_RATE));

    const toggleSection = (key) => {
        setVisibleSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const commitRate = () => {
        const parsed = Number.parseFloat(rateInput);
        if (Number.isNaN(parsed) || parsed < 0) {
            setRateInput(String(DEFAULT_DELOITTE_OVERAGE_RATE));
        }
    };

    const reset = () => {
        setVisibleSections(DEFAULT_VISIBLE_SECTIONS);
        setRateInput(String(DEFAULT_DELOITTE_OVERAGE_RATE));
        showToast({
            title: "Settings reset to defaults",
            type: "success",
            duration: 4000,
        });
    };

    return (
        <div className="ecr-portal-config__card" style={{ maxWidth: 560 }}>
            <div className="ecr-portal-config__card-header">
                <TuneOutlined fontSize="small" />
                <span>Copilot Dashboard Settings</span>
                <Typography
                    onClick={reset}
                    sx={{
                        marginLeft: "auto",
                        fontSize: "0.75rem",
                        color: "#888",
                        cursor: "pointer",
                        "&:hover": { color: ACCENT, textDecoration: "underline" },
                    }}
                >
                    Reset to defaults
                </Typography>
            </div>
            <Typography sx={{ fontSize: "0.78rem", color: "#999", mb: 1.5 }}>
                Applies to everyone. Choose which sections appear on the Copilot AI Credit dashboard —
                the same configuration currently editable from the dashboard&apos;s settings menu.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
                {Object.entries(SECTION_LABELS).map(([key, label]) => (
                    <FormControlLabel
                        key={key}
                        control={
                            <Checkbox
                                checked={visibleSections[key] ?? true}
                                onChange={() => toggleSection(key)}
                                size="small"
                                sx={{ color: "#ccc", "&.Mui-checked": { color: ACCENT }, py: 0.4 }}
                            />
                        }
                        label={<Typography sx={{ fontSize: "0.85rem", color: "#333" }}>{label}</Typography>}
                        sx={{ ml: 0, mr: 0 }}
                    />
                ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#555", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Deloitte Overage Rate
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#999", mb: 1 }}>
                Negotiated $/AIC rate applied to overage billing across the dashboard.
            </Typography>
            <TextField
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                onBlur={commitRate}
                onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                }}
                size="small"
                type="number"
                inputProps={{ step: "0.0001", min: 0 }}
                InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/AIC</InputAdornment>,
                }}
                sx={{
                    width: 220,
                    "& .MuiOutlinedInput-root": {
                        fontSize: "0.85rem",
                        "&.Mui-focused fieldset": { borderColor: ACCENT },
                    },
                }}
            />
        </div>
    );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const EcrOpsPortalConfig = () => {
    const [tab, setTab] = useState(0);

    return (
        <div>
            <div className="ecr-ops-page-header">
                <p className="ecr-ops-page-header__eyebrow">Configuration</p>
                <h1 className="ecr-ops-page-header__title">Portal Configuration</h1>
                <p className="ecr-ops-page-header__sub">
                    Manage Home Dashboard announcements and Copilot dashboard settings without a
                    frontend deploy.
                </p>
            </div>

            <div className="ecr-ops-content">
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    sx={{
                        mb: 2,
                        "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
                        "& .Mui-selected": { color: `${ACCENT} !important` },
                        "& .MuiTabs-indicator": { backgroundColor: ACCENT },
                    }}
                >
                    <Tab icon={<CampaignOutlined fontSize="small" />} iconPosition="start" label="Announcements" />
                    <Tab icon={<TuneOutlined fontSize="small" />} iconPosition="start" label="Dashboard Settings" />
                </Tabs>

                {tab === 0 ? <AnnouncementsTab /> : <DashboardSettingsTab />}
            </div>
        </div>
    );
};

export default EcrOpsPortalConfig;
